/**
 * Hook for initializing filters in the Users tab
 *
 * This hook handles the initialization of advanced filters for users.
 * It fetches filter options from APIs, applies personalization settings, and configures filters.
 *
 * Key features:
 * - Asynchronous loading of filter options from APIs
 * - Error handling for filter initialization failures
 * - User personalization support for saved filters
 * - Localization of filter labels
 * - Integration with filter cache for performance optimization
 *
 * @file use-filterinitialization.ts
 * @module Users/hooks
 */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { type MenuOption } from '@saviynt/design-system';

import {
  ADVANCED_FILTERS_METHODS,
  DEFAULT_AD_FILTER_PARAMS,
  ENTITLEMENT_USER_LIST_AD_FILTER,
  USERS_INITIAL_FILTERS,
} from '../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import {
  type ActiveFiltersState,
  type AdvancedFilterState,
} from '../../../../../../shared/types';
import { detailsActions } from '../../../../data-store/detailsSlice';

import useFilterCache from './use-filtercache';
import useGetQueryMap from './use-getQueryMap';

/**
 * Interface representing a Promise that can be unwrapped
 * @template T The type of value the promise resolves to
 */
interface UnwrappablePromise<T> extends Promise<T> {
  unwrap(): Promise<T>;
}

/**
 * Type definition for the query map object
 * Maps filter keys to their corresponding query functions
 */
type QueryMapType = Record<
  string,
  (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>
>;

/**
 * Interface representing a query item with its key, function and parameters
 */
interface QueryItem {
  key: string;
  fn: (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>;
  params: typeof DEFAULT_AD_FILTER_PARAMS;
}

/**
 * Interface for mapping query responses to success and error states
 */
interface ResponseMap {
  success: Record<string, unknown>;
  errors: Record<string, unknown>;
}

/**
 * Custom hook for initializing and managing advanced filters
 * @returns {Object} Object containing the initializeAdvancedFilters function
 *
 * @example
 * const { initializeAdvancedFilters } = useFiltersInitialization(
 *   queryMap,
 *   translateFunction
 * );
 */
const useFiltersInitialization = () => {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const { filterCache, filters } = useTypedSelector(
    (state) => state.aag.details.entitlementUsers.controls
  );

  // Hooks
  const queryMap = useGetQueryMap(true) as QueryMapType;
  const { setGroupInitialCache } = useFilterCache(
    filterCache,
    detailsActions.setEntitlementUsersFiltersCache
  );

  const initializeAdvancedFilters = useCallback(async (): Promise<boolean> => {
    const queryArr: QueryItem[] = Object.entries(queryMap).map(
      ([value, fn]) => ({
        key: value,
        fn,
        params: DEFAULT_AD_FILTER_PARAMS,
      })
    );

    try {
      const results = await Promise.allSettled(
        queryArr.map(({ params: params_2, fn }) => fn(params_2).unwrap())
      );
      // Mapping response to success and error
      const responseMap = results.reduce<ResponseMap>(
        (acc, result_1, index) => {
          const { key } = queryArr[index];

          if (result_1.status === 'fulfilled') {
            acc.success[key] = result_1.value;

            return acc;
          }

          acc.errors[key] = result_1.reason;

          return acc;
        },
        { success: {}, errors: {} }
      );

      // If any filter api fails then throw exception
      const erroredFilters = Object.keys(responseMap.errors);

      if (erroredFilters.length > 0) {
        throw Error(`Error in fetching filter(s) ${erroredFilters.join('')}`);
      }

      const cacheObjects: Record<string, MenuOption[]> = {};
      const savedObjects: ActiveFiltersState[] = [];

      // Configuring filters
      const initialFilters: AdvancedFilterState[] = Object.values(
        ENTITLEMENT_USER_LIST_AD_FILTER
      ).map((filter) => {
        const options = [];
        const isDefaultFilter = Boolean(
          (filter.value &&
            USERS_INITIAL_FILTERS.some(
              (initialFilter) => initialFilter.value === filter.value
            )) ||
            filters.find((_filter) => _filter.value === filter.value)?.isChecked
        );
        const isOptionFromApi =
          filter.method === ADVANCED_FILTERS_METHODS.API_WITH_OPTIONS &&
          responseMap.success[filter.value];
        const isFixedOption =
          filter.method === ADVANCED_FILTERS_METHODS.CUSTOM ||
          filter.method === ADVANCED_FILTERS_METHODS.FIXED;

        if (isOptionFromApi) {
          const successResponse = responseMap.success[
            filter.value
          ] as MenuOption[];

          options.push(...successResponse);

          cacheObjects[filter.value] = successResponse;
        }

        if (isFixedOption && filter.options) {
          const localizedOptions = filter.options.map((option) => ({
            ...option,
            label: l(option.label),
          }));

          options.push(...localizedOptions);
        }

        return {
          label: l(filter.label),
          value: filter.value,
          isDisabled: false,
          isChecked: isDefaultFilter,
          method: filter.method,
          category: filter.category,
          options,
        };
      });

      // Collect all active filters (both personalized and default checked filters)
      const allActiveFilters = [...savedObjects];

      // Add default checked filters that have options
      initialFilters.forEach((filter) => {
        if (
          filter.isChecked &&
          filter.options &&
          filter.options.length > 0 &&
          !savedObjects.some((saved) => saved.value === filter.value)
        ) {
          // For filters with fixed options, add checked options to activeFilters
          const checkedOptions = filter.options.filter(
            (option) => option.isChecked
          );

          if (checkedOptions.length > 0) {
            allActiveFilters.push({
              value: filter.value,
              method: filter.method,
              options: checkedOptions,
            });
          }
        }
      });

      // Set active filters
      dispatch(
        detailsActions.setEntitlementUsersActiveFilters(allActiveFilters)
      );

      // Initial cache
      setGroupInitialCache(cacheObjects, DEFAULT_AD_FILTER_PARAMS.search);

      // Dispatch filters with a small delay to ensure proper initialization
      setTimeout(() => {
        dispatch(detailsActions.setEntitlementUsersFilters(initialFilters));
      }, 0);

      return true;
    } catch (error) {
      Logger.log('Error fetching users filters', error);

      return false;
    }
  }, [l, queryMap, dispatch, setGroupInitialCache, filters]);

  return { initializeAdvancedFilters };
};

export default useFiltersInitialization;
